import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { CELL_KEYS, CELL_TYPES, cellValueFromGQL, gqlCellValueFromValue, isNoneCell } from "Backend";
import {
  Cell,
  CellType,
  CellValue,
  CellValueCountsView,
  FormField,
  GetCellValueCountsDocument,
  MigrateLiveCellTypeDocument,
} from "generated/graphql";
import { getCellValidationErrors, schemas, useForm } from "helpers/FormHelpers";
import { FormCheckbox, FormSelectField, FormTextField } from "helpers/FormHelpersComponents";

import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchLensAndRules,
  selectAllSubtypes,
  selectLensEditorAiCellConfigById,
  selectLensEditorAiFieldConfigById,
  selectLensEditorForm,
  selectLensEditorLensId,
  selectRules,
} from "redux/slices/lensEditor";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import Flex from "components/Flex";
import { distinct } from "helpers/coll";
import { Edit, List } from "@mui/icons-material";

import { FormFieldCellEditor } from "components/FormField/FormFieldCellEditor";
import RequiredCellDialog from "./RequiredCellDialog";
import SelectOptionDialog from "./SelectOptionDialog";
import RequestStatus from "redux/slices/RequestStatus";
import { LoadingButton } from "@mui/lab";
import { runGqlMutation, runGqlQuery } from "gqlClient";
import displayCellValue from "components/FormField/displayCellValue";
import TextCellValueMigrateDailog from "./TextCellValueMigrateDialog";
import MigrateLiveCellDialog from "./MigrateLiveCellDialog";
import { fetchForm } from "redux/slices/form.slice";
import CellAIConfigForm from "routes/lens-editor/profile-builder/CellAIConfigForm";
import { useSnackbar } from "notistack";
import SelectOptions from "components/SelectOptions/SelectOptions";
import { LensUpdateSnackbarContext } from "contexts/LensUpdateSnackbarContext";
import { CellFormKeys, CellFormState } from "./types";
import CellValidationForm from "./CellValidationForm";

interface CellEditorProps {
  cell?: CellFormState;
  field: FormField;
  rank: number;
  onCancel?: () => void;
  onSave: (cell: CellFormState) => void;
}

type ExistingCell = CellFormState & { id: number };
function isExistingCell(cell: CellFormState | undefined): cell is ExistingCell {
  return !!cell && cell.id !== undefined;
}

const isValidCellTypeMigration = (originalType: CellType | undefined, newType: any) => {
  const integerToFloat = originalType === CellType.Integer && newType === CellType.Float;
  const integerToText = originalType === CellType.Integer && newType === CellType.Text;
  const floatToText = originalType === CellType.Float && newType === CellType.Text;
  const selectToMultiselect = originalType === CellType.Select && newType === CellType.MultiSelect;
  // TODO: Support text-to-select mutation
  // const textToSelect = originalType == CellType.Text && newType == CellType.Select;
  return integerToFloat || integerToText || floatToText || selectToMultiselect;
};

const CellEditor = (props: CellEditorProps) => {
  const { cell, rank, onCancel, onSave, field } = props;
  const fieldId = field.id;
  const { live } = useAppSelector(selectLensEditorForm);
  const initialDefault = cellValueFromGQL(cell?.default || undefined);
  const [selectedSubtype, setSelectedSubtype] = useState<string | undefined>();
  const [showMigrateRequiredDialog, setShowMigrateRequiredDialog] = useState(false);
  const [showMigrateSelectOptionDialog, setMigrateSelectOptionDialog] = useState(false);
  const [showRenameTextCellValueDialog, setShowRenameTextCellValueDialog] = useState(false);
  const [showMigrateTextCellToSelectCellDialog, setShowMigrateTextCellToSelectCellDialog] = useState(false);
  const [showMigrateSelectCellToTextCellDialog, setShowMigrateSelectCellToTextCellDialog] = useState(false);
  const allSubtypes = useAppSelector(selectAllSubtypes);
  const allRules = useAppSelector(selectRules);
  const cellAiConfig = useAppSelector((state) => selectLensEditorAiCellConfigById(state, cell?.id));
  const fieldAiConfig = useAppSelector((state) => selectLensEditorAiFieldConfigById(state, fieldId));
  const { enqueueSnackbar } = useSnackbar();

  const cellForm = useForm<CellFormKeys>(
    {
      isValid: false,
      values: {
        name: cell?.name,
        type: cell?.type,
        selectOptions: cell?.selectOptions || [],
        optional: cell?.optional || false,
        default: initialDefault,
        key: cell?.key,
        exportExclude: cell?.exportExclude || false,
        systemName: cell?.systemName,
        description: cell?.description,
        analyticsExclude: cell?.analyticsExclude || false,
        validationConfig: cell?.validationConfig,
      },
      touched: {
        name: false,
        type: false,
        selectOptions: false,
        optional: true,
        default: false,
        key: false,
        exportExclude: true,
        systemName: false,
        description: false,
        analyticsExclude: true,
        validationConfig: false,
      },
      errors: {
        name: [],
        type: [],
        selectOptions: [],
        optional: [],
        default: [],
        key: [],
        exportExclude: [],
        systemName: [],
        description: [],
        validationConfig: [],
      },
    },
    {
      name: schemas.nonEmpty("Name"),
      type: schemas.nonEmpty("Type"),
      selectOptions: schemas.optionalArray,
      optional: { presence: true },
      default: schemas.customType(false),
      key: schemas.basicOptional(),
      exportExclude: { presence: true },
      // If we have an existing cell, we do not want to validate the systemName
      systemName: cell ? {} : schemas.systemName("System Name", field.cells),
      description: schemas.basicOptional(),
      analyticsExclude: schemas.customType(false),
      validationConfig: schemas.customType(false),
    }
  );
  // watch props.cell to see if it changes.. if it does, update the form
  useEffect(() => {
    if (props.cell) {
      cellForm.updateField("selectOptions")(props.cell.selectOptions || []);
      cellForm.updateField("optional")(props.cell.optional || false);
      cellForm.updateField("default")(cellValueFromGQL(props.cell.default || undefined));
      cellForm.updateField("type")(props.cell.type);
      cellForm.updateField("description")(props.cell.description);
      cellForm.updateField("analyticsExclude")(Boolean(props.cell.analyticsExclude));
      cellForm.updateField("validationConfig")(props.cell.validationConfig);
    }
  }, [props.cell]);
  const { updateField, formStateHandle } = cellForm;
  const formState = formStateHandle[0];
  const dispatch = useAppDispatch();
  const lensId = useAppSelector(selectLensEditorLensId);
  const [status, setStatus] = useState<RequestStatus>(RequestStatus.IDLE);
  const [cellValueCounts, setCellValueCounts] = useState<CellValueCountsView[] | undefined>(undefined);
  const anchorEl = useRef<HTMLButtonElement>(null);
  const [showValueCountsPopOver, setShowValueCountsPopOver] = useState(false);
  const { enqueueLensUpdateTimerSnackbar } = useContext(LensUpdateSnackbarContext);

  const handleUseDescriptions = () => {
    const descriptions = distinct(allRules.filter((r) => r.subtype === selectedSubtype).map((r) => r.description)).sort(
      (a, b) => a.toLowerCase().localeCompare(b.toLowerCase())
    );
    const existing = formState.values.selectOptions || [];
    cellForm.updateField("selectOptions")(existing.concat(descriptions));
  };

  const save = () => {
    const v = formState.values;

    let mappedDefault: CellValue | undefined = gqlCellValueFromValue(v.type, v.default);
    if (isNoneCell(mappedDefault)) {
      mappedDefault = undefined;
    }

    if (isExistingCell(cell) && isValidCellTypeMigration(cell.type, formState.values.type)) {
      runGqlMutation(MigrateLiveCellTypeDocument, {
        cellId: cell.id,
        newCellType: formState.values.type,
      }).then(() => {
        onSave({
          ...v,
          default: mappedDefault,
          fieldId,
          rank,
        });
        enqueueLensUpdateTimerSnackbar(lensId);
      });
    } else {
      onSave({
        ...v,
        default: mappedDefault,
        fieldId,
        rank,
      });
    }
  };

  const validationErrorMessage: string | undefined = useMemo(() => {
    const validationErrors = getCellValidationErrors(
      { value: formState.values.default, type: formState.values.type },
      formState.values.validationConfig
    );
    return validationErrors.length > 0 ? validationErrors[0] : undefined;
  }, [formState.values.type, formState.values.default, formState.values.validationConfig]);

  useEffect(() => {
    // Fields that we should clear if 'type' changes
    if (formState.values.type !== cell?.type) {
      if (formState.values.type !== CellType.Select && formState.values.type !== CellType.MultiSelect) {
        updateField("selectOptions")([]);
      }
      updateField("default")(initialDefault);
      updateField("validationConfig")(undefined);
    }
  }, [formState.values.type]);

  useEffect(() => {
    if (formState.values.name && !isExistingCell(cell)) {
      updateField("systemName")(
        formState.values.name
          .toLowerCase()
          .replace(/[^a-z0-9_]/g, "_")
          .substring(0, 63)
      );
    }
  }, [formState.values.name]);
  const isSelectType = formState.values.type === CellType.Select || formState.values.type === CellType.MultiSelect;
  const isTextType = formState.values.type === CellType.Text;
  const isNumericType = formState.values.type === CellType.Float || formState.values.type === CellType.Integer;
  const hasValidationConfig = isTextType || isNumericType;

  const getCellValueCount = (cellId: number) => {
    setStatus(RequestStatus.PENDING);

    runGqlQuery(GetCellValueCountsDocument, {
      lensId,
      cellId,
    })
      .then((resp) => {
        setStatus(RequestStatus.SUCCEEDED);
        const counts = resp.lens.formQueries.responseData.cellValueCounts;
        // Sort the counts in descending order
        const sortedCounts = counts.sort((a, b) => b.count - a.count);
        setCellValueCounts(sortedCounts);
      })
      .catch(() => {
        enqueueSnackbar("There was a problem loading the cell value counts. Please try again later.", {
          variant: "error",
        });

        setStatus(RequestStatus.FAILED);
      });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2">
        <p className="p-2 opacity-60 text-sm font-semibold uppercase">Cell Details</p>
        <div className="grid grid-cols-2 gap-2">
          <FormTextField label="Name" id="name" size="small" useForm={cellForm} />
          {isExistingCell(cell) && <TextField label="ID" disabled value={cell.id} />}
          <FormTextField label="System Name" id="systemName" useForm={cellForm} disabled={!!cell} />
          <FormSelectField label="Key" id="key" useForm={cellForm} options={CELL_KEYS} />
        </div>
        <FormTextField label="Description" id="description" useForm={cellForm} />
      </div>

      <div className="flex items-center">
        <FormCheckbox label="Optional" id="optional" disabled={live && cell && cell.optional} useForm={cellForm} />
        {
          // If form is new cell do not show migrate to required button
          isExistingCell(cell) && (
            <>
              <LoadingButton
                loading={status === RequestStatus.PENDING}
                onClick={() => {
                  setShowValueCountsPopOver(true);
                  if (!cellValueCounts) {
                    getCellValueCount(cell.id);
                  }
                }}
                variant="text"
                ref={anchorEl}
                color="secondary">
                Value Counts
              </LoadingButton>

              <Button
                onClick={() => setShowMigrateRequiredDialog(true)}
                variant="text"
                color="secondary"
                disabled={
                  // We can't migrate to required if the cell type has changed or if the cell is already required
                  formState.values.type !== cell?.type || !cell?.optional
                }>
                Migrate to Required
              </Button>
            </>
          )
        }

        <FormCheckbox label="Exclude on Export" id="exportExclude" useForm={cellForm} />
        <FormCheckbox label="Exclude from Analytics" id="analyticsExclude" useForm={cellForm} />
      </div>

      {cellValueCounts && anchorEl.current ? (
        <Popover
          id="value-counts"
          open={showValueCountsPopOver}
          anchorEl={anchorEl.current}
          onClose={() => setShowValueCountsPopOver(false)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}>
          <Box sx={{ p: 2 }}>
            <TableContainer style={{ maxHeight: 150 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Value</TableCell>
                    <TableCell>Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cellValueCounts.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{displayCellValue(row.value)}</TableCell>
                      <TableCell>{row.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Popover>
      ) : null}
      <Divider sx={{ mb: 1 }} />
      <div className="flex w-full">
        {isTextType && (
          <>
            <Tooltip title="Rename Existing Values">
              <IconButton
                size="small"
                onClick={() => {
                  setShowRenameTextCellValueDialog(true);
                  // load cell value counts for the dialog
                  if (!cellValueCounts && isExistingCell(cell)) {
                    getCellValueCount(cell.id);
                  }
                }}>
                <Edit />
              </IconButton>
            </Tooltip>
            <Tooltip title="Migrate To Select Cell">
              <IconButton
                size="small"
                onClick={() => {
                  setShowMigrateTextCellToSelectCellDialog(true);
                  // load cell value counts for the dialog
                  if (!cellValueCounts && isExistingCell(cell)) {
                    getCellValueCount(cell.id);
                  }
                }}>
                <List />
              </IconButton>
            </Tooltip>
          </>
        )}
        <FormSelectField label="Type" id="type" required options={CELL_TYPES} useForm={cellForm} fullWidth />
      </div>

      {isSelectType && (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              mx: 2,
            }}>
            <div className="grid">
              <Tooltip title="Rename Option" sx={{ mt: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setMigrateSelectOptionDialog(true);
                  }}>
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip title="Migrate To Text Cell">
                <IconButton
                  size="small"
                  onClick={() => {
                    setShowMigrateSelectCellToTextCellDialog(true);
                    // load cell value counts for the dialog
                    if (!cellValueCounts && isExistingCell(cell)) {
                      getCellValueCount(cell.id);
                    }
                  }}>
                  <List />
                </IconButton>
              </Tooltip>
            </div>
            <SelectOptions
              setOptions={cellForm.updateField("selectOptions")}
              options={formState.values.selectOptions}
            />
          </Box>

          <Flex ml={3} flexDirection="row" alignItems="center">
            <FormControl fullWidth size="small">
              <InputLabel>Autofill subtype</InputLabel>
              <Select
                value={selectedSubtype || null}
                onChange={(evt) => setSelectedSubtype(evt.target.value as string)}>
                {allSubtypes.map((subtype) => (
                  <MenuItem key={subtype} value={subtype}>
                    {subtype}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedSubtype && (
              <Button size="small" onClick={handleUseDescriptions}>
                Insert descriptions from {selectedSubtype}
              </Button>
            )}
          </Flex>
        </>
      )}
      {formState.values.type !== CellType.MultiSelect && (
        <FormFieldCellEditor
          label="Default"
          cell={{
            id: -1,
            fieldId: fieldId,
            name: "Default Value: " + formState.values.type,
            type: formState.values.type,
            selectOptions: formState.values.selectOptions,
            optional: true,
            key: undefined,
            exportExclude: false,
            rank: rank,
            systemName: "",
            analyticsExclude: false,
            validationConfig: formState.values.validationConfig,
          }}
          error={validationErrorMessage}
          value={formState.values.default}
          setValue={(cv) => {
            updateField("default")(cv);
          }}
          disabled={!formState.values.type}
        />
      )}
      {hasValidationConfig && (
        <>
          <Divider sx={{ mb: 1 }} />
          <h6 className="mb-2 text-xs font-semibold text-gray-200">Cell Validation</h6>
          <CellValidationForm useForm={cellForm} />
        </>
      )}
      <div className="flex justify-end">
        {onCancel && (
          <Button sx={{ mr: 2 }} color="default" onClick={onCancel}>
            Cancel
          </Button>
        )}

        <Button variant="contained" disabled={!formState.isValid || !!validationErrorMessage} onClick={save}>
          {cell
            ? isValidCellTypeMigration(cell?.type, formState.values.type)
              ? `Migrate ${cell?.type} to ${formState.values.type}${live ? " (Live Update!)" : ""}`
              : `Save${live ? " (Live Update!)" : ""}`
            : "Create"}
        </Button>
      </div>
      {!!fieldAiConfig && isExistingCell(cell) && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: "flex" }}>
            <CellAIConfigForm cellAiConfig={cellAiConfig} cellId={cell.id} />
          </Box>
        </>
      )}
      {showMigrateSelectOptionDialog && isExistingCell(cell) && (
        <SelectOptionDialog
          onClose={() => setMigrateSelectOptionDialog(false)}
          cell={cell as Cell}
          onSuccess={() => {
            // Close the dialog
            setMigrateSelectOptionDialog(false);
            // Reload the cell
            dispatch(fetchLensAndRules({ lensId }));
          }}
        />
      )}
      {showRenameTextCellValueDialog && isExistingCell(cell) && (
        <TextCellValueMigrateDailog
          onClose={() => setShowRenameTextCellValueDialog(false)}
          cell={cell as Cell}
          cellValueCounts={cellValueCounts || []}
          onSuccess={() => {
            // Close the dialog
            setShowRenameTextCellValueDialog(false);
            // Reload the cell
            void dispatch(fetchLensAndRules({ lensId }));
            // Refresh the cell value counts
            getCellValueCount(cell.id);
          }}
        />
      )}
      {showMigrateSelectCellToTextCellDialog && isExistingCell(cell) && (
        <MigrateLiveCellDialog
          newCellType={CellType.Text}
          title="Migrate to Text Type"
          confirmation="Are you sure you want to migrate this cell to Text type?"
          onClose={() => setShowMigrateSelectCellToTextCellDialog(false)}
          cell={cell as Cell}
          cellValueCounts={cellValueCounts || []}
          onSuccess={() => {
            // Close the dialog
            setShowMigrateSelectCellToTextCellDialog(false);
            // Reload the cell, void as the thunk handles the error
            void dispatch(fetchLensAndRules({ lensId }));
            // refresh the form values to match the new cell, void as the thunk handles the error
            void dispatch(fetchForm({ lensId }));
            // Refresh the cell value counts
            getCellValueCount(cell.id);
          }}
        />
      )}
      {showMigrateTextCellToSelectCellDialog && isExistingCell(cell) && (
        <MigrateLiveCellDialog
          newCellType={CellType.Select}
          title="Migrate to Select Type"
          confirmation="Are you sure you want to migrate this cell to Select type?"
          onClose={() => setShowMigrateTextCellToSelectCellDialog(false)}
          cell={cell as Cell}
          cellValueCounts={cellValueCounts || []}
          onSuccess={() => {
            // Close the dialog
            setShowMigrateTextCellToSelectCellDialog(false);
            // Reload the cell, void as the thunk handles the error
            void dispatch(fetchLensAndRules({ lensId }));
            // refresh the form values to match the new cell, void as the thunk handles the error
            void dispatch(fetchForm({ lensId }));
            // Refresh the cell value counts
            getCellValueCount(cell.id);
          }}
        />
      )}
      {showMigrateRequiredDialog && isExistingCell(cell) && (
        <RequiredCellDialog
          onClose={() => setShowMigrateRequiredDialog(false)}
          cell={cell as Cell}
          onSuccess={() => {
            // Close the dialog
            setShowMigrateRequiredDialog(false);
            // Reload the cell
            void dispatch(fetchLensAndRules({ lensId }));
          }}
        />
      )}
    </div>
  );
};

export default CellEditor;
